import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  TemplatesResponse,
  GetTemplatesResponse,
  CreateDraftOperationResponse,
  CreateDraftRequest,
  PublishTemplateRequest,
  PublishTemplateOperationResponse,
  CloneTemplateRequest,
  CloneTemplateOperationResponse,
  ActivateTemplateVersionRequest,
  ActivateTemplateVersionResponse,
  RenameTemplateRequest,
  RenameTemplateOperationResponse,
  DeleteTemplateRequest,
  DeleteTemplateOperationResponse,
  GetTemplatePresignedUrlRequest,
  GetTemplatePresignedUrlResponse,
} from "./templatesTypes";

export interface TemplatesState {
  loading: boolean;
  hasFetched: boolean;
  error?: string;
  dataByCategory: {
    OPTUM?: TemplatesResponse;
    PLATFORM?: TemplatesResponse;
    CLIENT?: TemplatesResponse;
  };
  lastRequestedCategory?: "OPTUM" | "PLATFORM" | "CLIENT";
  previewHtmlByKey: Record<string, string>;
  previewLoadingByKey: Record<string, boolean>;
  previewErrorByKey: Record<string, string | undefined>;
  presignedUrlByS3Path: Record<string, string>;
  presignedUrlLoadingByS3Path: Record<string, boolean>;
  presignedUrlErrorByS3Path: Record<string, string>;
  presignedUrlError: string | undefined;
  lastClientId?: string;
  lastOperation?:
    | { name: "getTemplates"; result?: GetTemplatesResponse }
    | { name: "renameTemplate"; result?: RenameTemplateOperationResponse }
    | { name: "deleteTemplate"; result?: DeleteTemplateOperationResponse }
    | { name: "createDraft"; result?: CreateDraftOperationResponse }
    | { name: "publishTemplate"; result?: PublishTemplateOperationResponse }
    | { name: "cloneTemplate"; result?: CloneTemplateOperationResponse }
    | {
        name: "activateTemplateVersion";
        result?: ActivateTemplateVersionResponse;
      };
  lastTemplateHtmlLoadDebug?: {
    source: "cache";
    templateId: string;
    versionValue: string;
    cacheKey: string;
    s3Path: string;
    at: number;
  };
}

const initialState: TemplatesState = {
  loading: false,
  hasFetched: false,
  dataByCategory: {},
  previewHtmlByKey: {},
  previewLoadingByKey: {},
  previewErrorByKey: {},
  presignedUrlByS3Path: {},
  presignedUrlLoadingByS3Path: {},
  presignedUrlErrorByS3Path: {},
  presignedUrlError: undefined,
  lastTemplateHtmlLoadDebug: undefined,
};

const templatesSlice = createSlice({
  name: "templates",
  initialState,
  reducers: {
    // Get Templates API
    getTemplatesRequested: (
      state,
      action: PayloadAction<{
        clientId?: string;
        platformId?: string;
        templateCategory?: "OPTUM" | "PLATFORM" | "CLIENT";
        token: string;
      }>,
    ) => {
      state.loading = true;
      state.hasFetched = false;
      state.error = undefined;
      state.lastRequestedCategory = action.payload.templateCategory;
      state.lastOperation = { name: "getTemplates" };
    },
    getTemplatesSucceeded: (
      state,
      action: PayloadAction<{
        data: GetTemplatesResponse;
        templateCategory?: "OPTUM" | "PLATFORM" | "CLIENT";
      }>,
    ) => {
      state.loading = false;
      state.hasFetched = true;
      state.error = undefined;

      const categoryToStore =
        action.payload.templateCategory || state.lastRequestedCategory;

      if (categoryToStore) {
        state.dataByCategory[categoryToStore] = action.payload.data;
      }

      state.lastOperation = {
        name: "getTemplates",
        result: action.payload.data,
      };
    },
    getTemplatesFailed: (state, action: PayloadAction<{ error: string }>) => {
      state.loading = false;
      state.hasFetched = true;
      state.error = action.payload.error;
    },

    // Rename Template API
    renameTemplateRequested: (
      state,
      _action: PayloadAction<RenameTemplateRequest>,
    ) => {
      state.loading = true;
      state.error = undefined;
      state.lastOperation = { name: "renameTemplate" };
    },
    renameTemplateSucceeded: (
      state,
      action: PayloadAction<{ result: RenameTemplateOperationResponse }>,
    ) => {
      state.loading = false;
      state.error = undefined;
      state.lastOperation = {
        name: "renameTemplate",
        result: action.payload.result,
      };
    },
    renameTemplateFailed: (state, action: PayloadAction<{ error: string }>) => {
      state.loading = false;
      state.error = action.payload.error;
      state.lastOperation = { name: "renameTemplate" };
    },

    // Delete Template API
    deleteTemplateRequested: (
      state,
      _action: PayloadAction<DeleteTemplateRequest>,
    ) => {
      state.loading = true;
      state.error = undefined;
      state.lastOperation = { name: "deleteTemplate" };
    },
    deleteTemplateSucceeded: (
      state,
      action: PayloadAction<{ result: DeleteTemplateOperationResponse }>,
    ) => {
      state.loading = false;
      state.error = undefined;
      state.lastOperation = {
        name: "deleteTemplate",
        result: action.payload.result,
      };
    },
    deleteTemplateFailed: (state, action: PayloadAction<{ error: string }>) => {
      state.loading = false;
      state.error = action.payload.error;
      state.lastOperation = { name: "deleteTemplate" };
    },

    // Publish Template API
    publishTemplateRequested: (
      state,
      _action: PayloadAction<PublishTemplateRequest>,
    ) => {
      state.loading = true;
      state.error = undefined;
      state.lastOperation = { name: "publishTemplate" };
    },
    publishTemplateSucceeded: (
      state,
      action: PayloadAction<{ result: PublishTemplateOperationResponse }>,
    ) => {
      state.loading = false;
      state.error = undefined;
      state.lastOperation = {
        name: "publishTemplate",
        result: action.payload.result,
      };
    },
    publishTemplateFailed: (
      state,
      action: PayloadAction<{ error: string }>,
    ) => {
      state.loading = false;
      state.error = action.payload.error;
      state.lastOperation = { name: "publishTemplate" };
    },

    // Clone Template API
    cloneTemplateRequested: (
      state,
      _action: PayloadAction<CloneTemplateRequest>,
    ) => {
      state.loading = true;
      state.error = undefined;
      state.lastOperation = { name: "cloneTemplate" };
    },
    cloneTemplateSucceeded: (
      state,
      action: PayloadAction<{ result: CloneTemplateOperationResponse }>,
    ) => {
      state.loading = false;
      state.error = undefined;
      state.lastOperation = {
        name: "cloneTemplate",
        result: action.payload.result,
      };
    },
    cloneTemplateFailed: (state, action: PayloadAction<{ error: string }>) => {
      state.loading = false;
      state.error = action.payload.error;
      state.lastOperation = { name: "cloneTemplate" };
    },

    // Create Draft Template API
    createDraftRequested: (
      state,
      _action: PayloadAction<CreateDraftRequest>,
    ) => {
      state.loading = true;
      state.error = undefined;
      state.lastOperation = { name: "createDraft" };
    },
    createDraftSucceeded: (
      state,
      action: PayloadAction<{ result: CreateDraftOperationResponse }>,
    ) => {
      state.loading = false;
      state.error = undefined;
      state.lastOperation = {
        name: "createDraft",
        result: action.payload.result,
      };
    },
    createDraftFailed: (state, action: PayloadAction<{ error: string }>) => {
      state.loading = false;
      state.error = action.payload.error;
      state.lastOperation = { name: "createDraft" };
    },

    // Activate Template Version API
    activateTemplateVersionRequested: (
      state,
      _action: PayloadAction<ActivateTemplateVersionRequest>,
    ) => {
      state.loading = true;
      state.error = undefined;
      state.lastOperation = { name: "activateTemplateVersion" };
    },
    activateTemplateVersionSucceeded: (
      state,
      action: PayloadAction<{ result: ActivateTemplateVersionResponse }>,
    ) => {
      state.loading = false;
      state.error = undefined;
      state.lastOperation = {
        name: "activateTemplateVersion",
        result: action.payload.result,
      };
    },
    activateTemplateVersionFailed: (
      state,
      action: PayloadAction<{ error: string }>,
    ) => {
      state.loading = false;
      state.error = action.payload.error;
      state.lastOperation = { name: "activateTemplateVersion" };
    },

    // Get Template Presigned URL API
    getTemplatePresignedUrlRequested: (
      state,
      action: PayloadAction<GetTemplatePresignedUrlRequest>,
    ) => {
      state.presignedUrlLoadingByS3Path[action.payload.s3Path] = true;
      state.presignedUrlError = undefined;
      delete state.presignedUrlErrorByS3Path[action.payload.s3Path];
    },
    getTemplatePresignedUrlSucceeded: (
      state,
      action: PayloadAction<{ s3Path: string; result: GetTemplatePresignedUrlResponse }>,
    ) => {
      state.presignedUrlLoadingByS3Path[action.payload.s3Path] = false;
      state.presignedUrlByS3Path[action.payload.s3Path] = action.payload.result.presignedUrl;
    },
    getTemplatePresignedUrlFailed: (
      state,
      action: PayloadAction<{ s3Path: string; error: string }>,
    ) => {
      state.presignedUrlLoadingByS3Path[action.payload.s3Path] = false;
      state.presignedUrlError = action.payload.error;
      state.presignedUrlErrorByS3Path[action.payload.s3Path] = action.payload.error;
    },

    fetchTemplatePreviewRequested: (
      state,
      action: PayloadAction<{ cacheKey: string; src: string }>,
    ) => {
      state.previewLoadingByKey[action.payload.cacheKey] = true;
      state.previewErrorByKey[action.payload.cacheKey] = undefined;
    },
    fetchTemplatePreviewSucceeded: (
      state,
      action: PayloadAction<{ cacheKey: string; html: string }>,
    ) => {
      state.previewLoadingByKey[action.payload.cacheKey] = false;
      state.previewErrorByKey[action.payload.cacheKey] = undefined;
      state.previewHtmlByKey[action.payload.cacheKey] = action.payload.html;
    },
    fetchTemplatePreviewFailed: (
      state,
      action: PayloadAction<{ cacheKey: string; error: string }>,
    ) => {
      state.previewLoadingByKey[action.payload.cacheKey] = false;
      state.previewErrorByKey[action.payload.cacheKey] = action.payload.error;
    },

    // Debug signal for Redux DevTools: HTML was served from Redux cache.
    templateHtmlServedFromCache: (
      state,
      action: PayloadAction<{
        templateId: string;
        versionValue: string;
        cacheKey: string;
        s3Path: string;
      }>,
    ) => {
      state.lastTemplateHtmlLoadDebug = {
        source: "cache",
        templateId: action.payload.templateId,
        versionValue: action.payload.versionValue,
        cacheKey: action.payload.cacheKey,
        s3Path: action.payload.s3Path,
        at: Date.now(),
      };
    },

    clearTemplates: (state) => {
      state.error = undefined;
      state.loading = false;
      state.dataByCategory = {};
      state.lastRequestedCategory = undefined;
      state.previewHtmlByKey = {};
      state.previewLoadingByKey = {};
      state.previewErrorByKey = {};
      state.presignedUrlByS3Path = {};
      state.presignedUrlLoadingByS3Path = {};
      state.presignedUrlErrorByS3Path = {};
      state.presignedUrlError = undefined;
      state.lastTemplateHtmlLoadDebug = undefined;
      state.lastClientId = undefined;
      state.lastOperation = undefined;
    },
  },
});

export const templatesActions = templatesSlice.actions;
export const templatesReducer = templatesSlice.reducer;
